import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import {
  formatDate,
  getCmsOptimizedUrl,
  RichTextGlobal,
} from '@rumblefish/ui';
import { Layout } from '@rumblefish/ui/Rumblefish23Theme';

import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getBlogData } from '../../utils/getBlogData';
import { normalizeCategoryName } from '../blogPage/BlogPage.utils';
import { NextHeadWithMeta } from '../NextHeadWithMeta';
import {
  blogArticleImageHeight,
  classes,
  StyledBlogArticle,
} from './BlogArticle.styles';
import type { BlogArticleProps } from './BlogArticle.types';
import { LatestArticles } from './LatestArticles';
import { TableOfContents } from './TableOfContents';
import { ReadingProgressBar } from './ReadingProgressBar';

export const BlogArticle = ({ blogPosts }: BlogArticleProps) => {
  const router = useRouter();

  const theme = useTheme();

  const { blogPost, latestBlogPosts } = getBlogData({
    blogPosts,
    router,
  });

  const title = `Rumble Fish | Blog | ${blogPost?.title}`;

  const facebookImage = blogPost?.facebookImage
    ? blogPost.facebookImage?.url
    : blogPost?.image?.url;

  const meta = [
    {
      name: 'description',
      content: blogPost?.shortContent ? blogPost?.shortContent : '',
    },
    {
      name: 'keywords',
      content: blogPost?.category?.map((el) => el?.name).join(', '),
    },
    { name: 'og:title', content: blogPost?.title },
    { name: 'og:image', content: facebookImage },
  ];

  useEffect(() => {
    if (!blogPost) {
      router.push('/blog');
    }
  }, [blogPost, router]);

  if (!blogPost) return null;

  return (
    <Layout blogPosts={blogPosts}>
      <ReadingProgressBar />
      <NextHeadWithMeta meta={meta} title={title} />
      <StyledBlogArticle>
        <div className={clsx(classes.articleLayout)}>
          <div className={clsx(classes.article)}>
            <img
              className={clsx(classes.articleImage, {
                [classes.articleImageContain]: blogPost?.imageFitContain,
              })}
              src={getCmsOptimizedUrl({
                url: blogPost?.image?.url ? blogPost.image.url : '',
                height: blogArticleImageHeight * 2,
                quality: 70,
              })}
              alt={blogPost?.title}
            />

            <Box
              px={{ xs: 2, md: 4 }}
              sx={{ maxWidth: '72ch', mx: 'auto' }}>
              <Typography className={classes.title} variant="h2">
                {blogPost?.title}
              </Typography>

              <Typography className={classes.textPubDate}>
                <span style={{ marginRight: '16px' }}>
                  {formatDate(blogPost?.pubDate)}
                </span>
                {'\n\u2022'}
                <span style={{ marginLeft: '16px' }}>
                  {blogPost?.timeToRead + ' min read'}
                </span>
              </Typography>

              <Stack mt={1.5} mb={3} direction="row" gap={1} flexWrap="wrap">
                {blogPost.category?.map((category) => (
                  <Link
                    href={`/blog/category/${normalizeCategoryName(
                      category?.name || ''
                    )}`}
                    key={category?.name}>
                    <Chip
                      label={category?.name}
                      sx={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        color: theme.palette.common.white,
                        fontWeight: 600,
                        fontSize: '12px',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.14)',
                        },
                      }}
                    />
                  </Link>
                ))}
              </Stack>

              {blogPost?.content && (
                <div className={classes.richTextContainer}>
                  <RichTextGlobal
                    content={blogPost.content}
                    embeddedItems={blogPost.embeddedInRichText}
                  />
                </div>
              )}

              {blogPost?.authors && blogPost.authors.length > 0 && (
                <Box mt={4} mb={3} display="flex" flexDirection="column" gap={2}>
                  {blogPost.authors.map((author) => (
                    <Box
                      key={author?.documentId}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '16px',
                        p: 2,
                      }}>
                      {author?.Avatar?.url && (
                        <img
                          src={getCmsOptimizedUrl({
                            url: author.Avatar.url,
                            width: 120,
                          })}
                          alt={author.Name ?? ''}
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: 'white' }}>
                          {author?.Name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '13px',
                          }}>
                          {author?.Position}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </div>

          <div className={clsx(classes.tocSidebar)}>
            {blogPost?.content && (
              <TableOfContents content={blogPost.content} />
            )}
          </div>
        </div>

        <LatestArticles latestBlogPosts={latestBlogPosts} />
      </StyledBlogArticle>
    </Layout>
  );
};
